import React from 'react';

/**
 * Tier badge with "TC" logo. Currently only Platinum tier.
 * Future tiers: gold, silver, bronze.
 */

const TIER_STYLES = {
  founder: {
    ring: 'linear-gradient(135deg, #B8923F 0%, #E6C77A 25%, #FBEBB5 50%, #E6C77A 75%, #B8923F 100%)',
    badge: 'linear-gradient(135deg, #B8923F 0%, #E6C77A 50%, #B8923F 100%)',
    text: '#08241C',
    shadow: 'rgba(184,146,63,0.45)',
    label: 'Founder',
  },
  platinum: {
    ring: 'linear-gradient(135deg, #A8B4C0 0%, #D6DCE2 25%, #E8ECF0 50%, #D6DCE2 75%, #A8B4C0 100%)',
    badge: 'linear-gradient(135deg, #B8C4CF 0%, #E2E8ED 50%, #B8C4CF 100%)',
    text: '#3A3F47',
    shadow: 'rgba(168,180,192,0.4)',
    label: 'Platinum',
  },
  gold: {
    ring: 'linear-gradient(135deg, #C8A96A 0%, #E8D5A8 25%, #F0E4C4 50%, #E8D5A8 75%, #C8A96A 100%)',
    badge: 'linear-gradient(135deg, #C8A96A 0%, #E8D5A8 50%, #C8A96A 100%)',
    text: '#08241C',
    shadow: 'rgba(200,169,106,0.4)',
    label: 'Gold',
  },
  silver: {
    ring: 'linear-gradient(135deg, #8E9AAA 0%, #C4CCD6 25%, #D8DFE6 50%, #C4CCD6 75%, #8E9AAA 100%)',
    badge: 'linear-gradient(135deg, #8E9AAA 0%, #C4CCD6 50%, #8E9AAA 100%)',
    text: '#2A2F35',
    shadow: 'rgba(142,154,170,0.3)',
    label: 'Silver',
  },
  bronze: {
    ring: 'linear-gradient(135deg, #A0724E 0%, #D4A574 25%, #E8C4A0 50%, #D4A574 75%, #A0724E 100%)',
    badge: 'linear-gradient(135deg, #A0724E 0%, #D4A574 50%, #A0724E 100%)',
    text: '#3A2010',
    shadow: 'rgba(160,114,78,0.3)',
    label: 'Bronze',
  },
};

/**
 * Small "TC" badge circle — placed at bottom-right of avatars.
 */
const TierBadge = ({ tier = 'platinum', size = 'md' }) => {
  const s = TIER_STYLES[tier] || TIER_STYLES.platinum;
  const dims = { sm: 16, md: 20, lg: 26 }[size] || 20;
  const fontSize = { sm: 6, md: 7, lg: 9 }[size] || 7;

  return (
    <div
      className="flex items-center justify-center rounded-full border-2"
      style={{
        width: dims,
        height: dims,
        background: s.badge,
        borderColor: '#fff',
        boxShadow: `0 1px 4px ${s.shadow}`,
      }}
      data-testid="tier-badge"
      title={tier === 'founder' ? 'Founder of The Club' : `${s.label} Member`}
    >
      <span style={{ fontSize, fontWeight: 900, color: s.text, letterSpacing: '-0.02em', lineHeight: 1 }}>
        TC
      </span>
    </div>
  );
};

/**
 * Pill badge with "TC" + tier label — for Discover cards, Settings, etc.
 */
const TierPill = ({ tier = 'platinum', size = 'sm' }) => {
  const s = TIER_STYLES[tier] || TIER_STYLES.platinum;
  const px = size === 'xs' ? 'px-1.5 py-0.5' : 'px-2.5 py-1';
  const textSize = size === 'xs' ? 'text-[8px]' : 'text-[10px]';

  return (
    <span
      className={`inline-flex items-center gap-1 rounded-full font-bold ${px} ${textSize}`}
      style={{
        background: s.badge,
        color: s.text,
        boxShadow: `0 2px 8px ${s.shadow}`,
      }}
      data-testid={tier === 'founder' ? 'founder-pill' : 'tier-pill'}
      title={tier === 'founder' ? 'Founder of The Club' : `${s.label} Member`}
    >
      <span style={{ fontWeight: 900, letterSpacing: '-0.02em' }}>TC</span>
      <span>{s.label}</span>
    </span>
  );
};

/** Highest tier a user holds, or null. Founder outranks Platinum. */
const getUserTier = (user) => {
  if (!user) return null;
  if (user.is_founder) return 'founder';
  if (user.is_platinum) return 'platinum';
  return null;
};

export { TIER_STYLES, TierBadge, TierPill, getUserTier };
export default TierBadge;
